/**
 * React server side rendering shared functionality
 * @module react-universal/server
 */
/* eslint-disable react/jsx-filename-extension */
import React from 'react'
import { hydrateRoot } from 'react-dom/client'
import _get from 'lodash/get'
import _isDate from 'lodash/isDate'
import { get as getCookie } from 'cookies-js'
import { getAppData } from './react-universal/browser'
import { get as getConfig } from './config/index'
import { setLogger, getLogger } from './log/index'
import { getLogger as getLoggerBrowser } from './log/browser'
import { createStore } from './store/configureStore'
import { initialState } from './reducers/index'
import { AUTH_COOKIE_NAME, setTokenDataInStorage } from './services/auth/index'
import { getViewport } from './services/app/_viewport'
import { createHistory, parseLocation } from './services/resolver/index'
import { isLogout } from './services/url/index'
import attachResolverHistoryListener from './browser/attach-resolver-history-listener'
import { getUserRequestCountry } from './services/user/index'
import App from './components/App/index'
import { getSessionStorage, SESSION_STORAGE_GAIA_PLAN_SELECTION, SESSION_STORAGE_GAIA_CHECKOUT_DISCOUNT_CODE } from './services/local-preferences/index'
import { getAnonymousUuid } from './browser/anonymous'
import { init as initNewRelic } from './browser/new-relic'
import { isLocal } from './environment'

const config = getConfig()
const ENV_LOCAL = isLocal()

/**
 * Reload the page when the data is not syned between
 * the hydrated data and the cookies to prevent inconsitent
 * behavior in the application
 */
function reloadNotSynced () {
  // Don't block the logout page.
  if (isLogout(
    _get(window, ['location', 'pathname']),
  )) {
    return
  }

  try {
    const appData = getAppData()
    const authCookie = getCookie(AUTH_COOKIE_NAME)
    const auth = authCookie ? JSON.parse(authCookie) : {}
    const appAuth = _get(appData, ['hydrate', 'auth'])
    const appJwt = _get(appAuth, ['jwt'])
    const cookieJwt = _get(auth, 'jwt')
    if (cookieJwt !== appJwt) {
      // Write the app auth to the cookies
      // which will also clean duplicates
      setTokenDataInStorage(appAuth)
      // Prevent reload in local environment due to dev tools causing infinite reloads
      if (ENV_LOCAL) {
        return
      }
      const lastAuthWindowReloadDateStoreValue = sessionStorage.getItem('auth-window-reload-date')
      let shouldReload = false
      if (!lastAuthWindowReloadDateStoreValue) {
        shouldReload = true
      } else {
        const lastAuthWindowReloadDate = new Date(lastAuthWindowReloadDateStoreValue)
        if (_isDate(lastAuthWindowReloadDate)) {
          const now = new Date()
          const diff = now.getTime() - lastAuthWindowReloadDate.getTime()
          if (diff >= 15 * 60 * 1000) { // 15 minutes in milliseconds
            shouldReload = true
          }
        } else {
          shouldReload = true
        }
      }
      if (shouldReload) {
        sessionStorage.setItem('auth-window-reload-date', new Date().toISOString())
        window.location.reload()
      }
    }
  } catch (err) {
    const log = getLogger()
    log.error(err, 'Failed parse auth cookie data in reloadNotSynced')
  }
}

/**
 * Initialize a buyan logger and set it in the library
 * as a singleton
 */
function initLogger () {
  const appData = getAppData()
  const uid = _get(appData, ['hydrate', 'auth', 'uid'])
  const logger = getLoggerBrowser(uid)
  setLogger(logger)
  return logger
}

function renderApp (options = {}) {
  const { store, history } = options
  return (
    <App
      store={store}
      history={history}
      location={global.location}
    />
  )
}

/**
 * The entry point for the browser code in the Web App.
 */
export async function run () {
  const {
    log: {
      browser: {
        level: logLevel,
      } = {},
    } = {},
  } = config
  const startTime = new Date().getTime()
  const logger = initLogger()

  function logBrowserError (error, name) {
    logger.error(error, `Browser run failed for ${name}`)
  }

  try {
    window.addEventListener('unhandledrejection', (e) => {
      e.preventDefault()
      const reason = _get(e, ['reason'])
      const detailReason = _get(e, ['detail', 'reason'])
      const log = logger || console
      log.error(detailReason || reason || 'Unknown reason in unhandledrejection')
    })

    initNewRelic({ appData: getAppData() })
    reloadNotSynced()

    const appData = getAppData()
    const history = createHistory()
    const hydrate = _get(appData, 'hydrate', {})
    const isWebView = _get(hydrate, 'app.isWebView', false)
    const rawAuth = _get(hydrate, 'auth')
    const historyLocation = parseLocation(_get(history, 'location', {}))

    hydrate.app.client = true
    hydrate.app.server = false
    hydrate.app.bootstrapPhase = null
    hydrate.app.viewport = getViewport()
    hydrate.app.bootstrapComplete = false
    hydrate.app.initializationStartTime = startTime

    hydrate.app.renderFooter = !rawAuth && !isWebView
    hydrate.app.renderHeader = !rawAuth && !isWebView
    hydrate.app.renderApp = !rawAuth

    if (!rawAuth) {
      const uuid = await getAnonymousUuid()
      hydrate.user = hydrate.user || {}
      hydrate.user.data = hydrate.user.data || {}
      hydrate.user.data.anonymousUuid = uuid
    }

    /**
     * Hydrate the gaia plan selection from session storage if it exists
     * this gives persistence of the plan selection across page reloads
     * which is important for the gaia checkout flow
     */
    const gaiaPlanSelection = getSessionStorage(SESSION_STORAGE_GAIA_PLAN_SELECTION)
    if (gaiaPlanSelection) {
      hydrate.plans = hydrate.plans || {}
      hydrate.plans.selection = gaiaPlanSelection
    }

    const gaiaCheckoutDiscountCode = getSessionStorage(SESSION_STORAGE_GAIA_CHECKOUT_DISCOUNT_CODE)
    if (gaiaCheckoutDiscountCode) {
      hydrate.checkout = hydrate.checkout || {}
      hydrate.checkout.discountCode = gaiaCheckoutDiscountCode
    }

    // set the user's country that they are making requests from
    const { requestCountry } = await getUserRequestCountry()
    hydrate.user.data.requestCountry = requestCountry

    /**
     * The location information for the resolver needs to be rebuilt here due to caching issues,
     * primarily with query parameters. If a page is cached by the server, then the server side
     * hydration will contain the incorrect query parameters. This will now use the location
     * parameters provided by the client on app bootstrap.
     */
    hydrate.resolver.location = historyLocation
    hydrate.resolver.path = _get(historyLocation, 'pathname', '')
    hydrate.resolver.query = _get(historyLocation, 'query', {})
    hydrate.resolver.processing = false

    const store = await createStore({
      initialState, logLevel, hydrate, history,
    })

    history.listen((location) => (
      attachResolverHistoryListener({ store, location })
    ))

    try {
      const appElement = document.getElementById('app')
      const component = renderApp({ store, history })
      hydrateRoot(appElement, component)
    } catch (err) {
      logBrowserError(err, 'app')
    }
  } catch (e) {
    logger.error(e)
  }
}

export default run
